<template>
 <v-container style="padding-bottom:0px">
   <v-expansion-panels v-model="parentPanel" class="mt-2">
     <v-expansion-panel id="documents-panel">
       <v-expansion-panel-title>Documents, Policies & Agreements</v-expansion-panel-title>
       <v-expansion-panel-text>
         <div class="accordion-container">
           <!-- Status Summary at Top -->
           <v-alert
             v-if="company"
             :color="allRequiredDocumentsAccepted ? 'success' : 'info'"
             variant="tonal"
             class="mb-6"
           >
             <div class="d-flex align-center">
               <v-icon class="mr-2">
                 {{ allRequiredDocumentsAccepted ? 'mdi-check-circle' : 'mdi-information' }}
               </v-icon>
               <span>
                 {{ allRequiredDocumentsAccepted
                   ? 'All required documents have been accepted'
                   : 'Please review and accept all required documents to continue'
                 }}
               </span>
             </div>
           </v-alert>

           <!-- Individual Document Panels -->
           <v-expansion-panels v-model="expandedDocuments" multiple>
             <v-expansion-panel
               v-for="doc in documents"
               :key="doc.id"
               class="document-panel mb-4"
             >
               <v-expansion-panel-title>
                 <div class="d-flex align-center">
                   <v-icon class="mr-2" :color="getStatusColor(doc)">
                     {{ getStatusIcon(doc) }}
                   </v-icon>
                   <div>
                     <div class="text-h6">{{ doc.title }}</div>
                     <div class="text-caption">
                       {{ doc.required ? 'Required' : 'Optional' }}
                       <span v-if="doc.version" class="ml-2">• Version {{ doc.version }}</span>
                     </div>
                   </div>
                   <v-spacer></v-spacer>
                   <v-chip
                     v-if="isDocumentAccepted(doc)"
                     color="success"
                     size="small"
                     class="mr-4"
                   >
                     Accepted
                   </v-chip>
                 </div>
               </v-expansion-panel-title>

               <v-expansion-panel-text>
                 <div class="document-content mb-6" v-html="doc.content">
                 </div>

                 <v-divider class="mb-4"></v-divider>

                 <!-- Order Form for Customer Agreement -->
                 <CustomerAgreementOrderForm v-if="doc.id === 'customeragreement'" />

                 <!-- Acceptance Section -->
                 <div v-if="doc.type === 'checkbox'" class="checkbox-acceptance-section">
                   <div class="checkbox-container">
                     <v-checkbox
                       v-model="acceptedDocs[doc.id]"
                       :label="`I have read and accept the ${doc.title}`"
                       color="primary"
                       hide-details
                       @change="handleDocumentAcceptance(doc, acceptedDocs[doc.id])"
                     ></v-checkbox>
                     <div v-if="acceptedDocs[doc.id]" class="checkbox-date-display">
                       <div class="checkbox-current-date">{{ getCurrentDate() }}</div>
                       <div class="checkbox-underline"></div>
                     </div>
                   </div>
                 </div>

                 <div v-else-if="doc.type === 'signature'" class="signature-section">
                   <p class="text-subtitle-1 mb-2">Please sign to accept {{ doc.title }}</p>
                   <div class="input-container">
                     <div class="signature-field">
                       <v-text-field
                         v-model="signatureInputs[doc.id]"
                         label="Type your full name to sign"
                         placeholder="Your full legal name"
                         variant="outlined"
                         :rules="[v => !!v || 'Signature is required']"
                       ></v-text-field>
                       <v-btn
                         color="primary"
                         class="mt-2"
                         :disabled="!isValidSignature(doc.id)"
                         @click="submitSignature(doc)"
                       >
                         Accept and Sign
                       </v-btn>
                     </div>
                     <div v-if="signatures[doc.id]" class="signature-date-display">
                       <div class="signature-current-date">{{ getCurrentDate() }}</div>
                       <div class="signature-underline"></div>
                     </div>
                   </div>
                   <p class="text-caption mt-1" v-if="!doc.id === 'customeragreement'">
                     By typing your name above, you acknowledge this constitutes a legal signature.
                   </p>
                 </div>
                   <div style="margin-top: 14px; margin-bottom: 8px;" v-if="doc.id === 'customeragreement'">
                    <h3>Acknowledgment and Authorization</h3>
                    <p style="font-weight: bold;color: #808080">By signing above, you affirm that you are duly authorized to sign on behalf of your organization. You further confirm that you have reviewed and accept the entire BBAS Customer Agreement, including the terms and conditions outlined within, as well as the associated order form, policies, notices, and any amendments, and exhibits. By signing, you agree that your organization is legally bound by the terms of the BBAS Customer Agreement.</p>
                   </div>

                 <!-- Download/Print Options -->
                 <v-card-actions class="mt-4">
                   <v-btn
                     prepend-icon="mdi-download"
                     variant="text"
                     @click="downloadDocument(doc)"
                     class="mr-2"
                   >
                     Download PDF
                   </v-btn>
                   <v-btn
                     prepend-icon="mdi-printer"
                     variant="text"
                     @click="printDocument(doc)"
                   >
                     Print
                   </v-btn>
                 </v-card-actions>
               </v-expansion-panel-text>
             </v-expansion-panel>
           </v-expansion-panels>
         </div>
       </v-expansion-panel-text>
     </v-expansion-panel>
   </v-expansion-panels>
 </v-container>
 <div class="bottom-spacer"></div>

</template>

<script>
import { ref, computed } from 'vue';
import moment from 'moment';
import axios from 'axios';
import { useStore } from 'vuex';
import CustomerAgreementOrderForm from './CustomerAgreementOrderForm.vue';

// Import HTML documents using raw-loader
import customerAgreementContent from '!raw-loader!@/documents/customer-agreement.html';
import privacyPolicyContent from '!raw-loader!@/documents/privacy-policy.html';
import cookiePolicyContent from '!raw-loader!@/documents/cookie-policy.html';
import lawEnforcementDataRequestGuidelines from '!raw-loader!@/documents/law-enforcement-data-request-guidelines.html'
import endUserConductAndConductPolicy from '!raw-loader!@/documents/end-user-conduct-and-content-policy.html'
import dataDeletionPolicy from '!raw-loader!@/documents/data-deletion-policy.html'
import dataProcessingNotice from '!raw-loader!@/documents/data-processing-notice.html'

export default {
name: 'CompanyAccountDocumentsPoliciesAgreements',

components: {
  CustomerAgreementOrderForm
},

props: {
  company: {
    type: Object,
    required: true
  }
},

setup(props) {
  const parentPanel = ref(null);
  const expandedDocuments = ref([]);
  const acceptedDocs = ref({});
  const signatures = ref({});
  const signatureInputs = ref({});

  const store = useStore();
  const selectedUser = computed(() => store.state.selectedUser);

  function getCurrentDate() {
    return moment().format('MMMM D, YYYY');
  }

  const documents = [
    {
      id: 'customeragreement',
      title: 'Customer Agreement',
      type: 'signature',
      content: customerAgreementContent,
      required: true,
      version: '2.1'
    },
    {
      id: 'privacypolicy',
      title: 'Privacy Policy',
      type: 'checkbox',
      content: privacyPolicyContent,
      required: true,
      version: '1.2'
    },
    {
      id: 'cookiepolicy',
      title: 'Cookie Policy',
      type: 'checkbox',
      content: cookiePolicyContent,
      required: true,
      version: '1.0'
    },
    {
      id: 'lawenforcementdatarequestguidlines',
      title: 'Law Enforcement Data Request Guidelines',
      type: 'notice',
      content: lawEnforcementDataRequestGuidelines,
      required: false,
      version: '1.0'
    },
    {
      id: 'endusercontentandconductpolicy',
      title: 'End User Content & Conduct Policy',
      type: 'checkbox',
      content: endUserConductAndConductPolicy,
      required: true,
      version: '1.0'
    },
    {
      id: 'datadeletionpolicy',
      title: 'Data Deletion Policy',
      type: 'notice',
      content: dataDeletionPolicy,
      required: false,
      version: '1.0'
    },
    {
      id: 'dataprocessingnotice',
      title: 'Data Processing Notice',
      type: 'notice',
      content: dataProcessingNotice,
      required: false,
      version: '1.0'
    }

  ];

  const allRequiredDocumentsAccepted = computed(() => {
    return documents
      .filter(doc => doc.required)
      .every(doc => isDocumentAccepted(doc));
  });

  function isDocumentAccepted(doc) {
    if (doc.type === 'notice') return true;
    return doc.type === 'checkbox' 
      ? acceptedDocs.value[doc.id] 
      : !!signatures.value[doc.id];
  }

  function isValidSignature(docId) {
    const signature = signatureInputs.value[docId];
    return signature && signature.trim().length >= 2 && /^[A-Za-z\s.-]+$/.test(signature);
  }

  function getStatusIcon(doc) {
    if (doc.type === 'notice') return 'mdi-information';
    if (isDocumentAccepted(doc)) {
      return 'mdi-check-circle';
    }
    return doc.required ? 'mdi-file-alert' : 'mdi-file-outline';
  }

  function getStatusColor(doc) {
    if (doc.type === 'notice') return 'grey';
    if (isDocumentAccepted(doc)) {
      return 'success';
    }
    return doc.required ? 'warning' : 'grey';
  }

  function formatDate(date) {
    return moment(date).format('MMMM D, YYYY [at] h:mm A');
  }

  async function submitSignature(doc) {
    const signature = signatureInputs.value[doc.id];
    if (!isValidSignature(doc.id)) return;
    
    signatures.value[doc.id] = signature;
    await handleDocumentAcceptance(doc, signature);
  }

  async function loadDocumentAcceptances() {
    try {
      if (!props.company?.companyNumber) return;
      
      const response = await axios.get(`/portaldb/db/documentAcceptance/${props.company.companyNumber}`);
      const acceptances = response.data;

      documents.forEach(doc => {
        const docAcceptances = acceptances
          .filter(a => a.documentId === doc.id && a.documentVersion === doc.version)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        if (docAcceptances.length > 0) {
          const latest = docAcceptances[0];
          if (latest.state === 'accepted') {
            if (doc.type === 'checkbox') {
              acceptedDocs.value[doc.id] = true;
            } else if (doc.type === 'signature') {
              signatures.value[doc.id] = latest.signature;
              signatureInputs.value[doc.id] = latest.signature;
            }
          }
        }
      });
    } catch (error) {
      console.error('Error loading document acceptances:', error);
    }
  }

   async function handleDocumentAcceptance(doc, value) {
     try {
       if (!props.company?.companyNumber) return;

       const acceptanceData = {
         documentId: doc.id,
         documentVersion: doc.version,
         companyNumber: props.company.companyNumber,
         userUid: selectedUser.value.uid,
         name: selectedUser.value.name,
         state: value ? 'accepted' : 'revoked',
         signature: doc.type === 'signature' ? value : null
       };

       await axios.post('/portaldb/db/documentAcceptance', acceptanceData);
       
     } catch (error) {
       console.error('Error saving document acceptance:', error);
     }
   }

   const downloadDocument = (doc) => {
     console.log('Downloading document:', doc.title);
     const downloadUrl = `/portaldb/db/documents/${doc.id}/download`;
     window.open(downloadUrl, '_blank');
   };

  const printDocument = (doc) => {
    console.log('Printing document:', doc.title);
    window.print();
  };

  loadDocumentAcceptances();

  return {
    parentPanel,
    expandedDocuments,
    acceptedDocs,
    signatures,
    signatureInputs,
    documents,
    allRequiredDocumentsAccepted,
    isDocumentAccepted,
    isValidSignature,
    getStatusIcon,
    getStatusColor,
    formatDate,
    handleDocumentAcceptance,
    submitSignature,
    downloadDocument,
    printDocument,
    getCurrentDate
  };
}
};
</script>

<style scoped>

.bottom-spacer {
  height: 300px;
  width: 100%;
}

.accordion-container {
 background-color: #f5f5dc;
 padding: 14px;
 border-radius: 8px;
}

.document-content {
 font-size: 16px;
 line-height: 1.6;
 color: #333;
}

.signature-section {
 margin-top: 50px;
 max-width: 670px;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
 font-size: 1.25rem !important;
 font-weight: 600 !important;
 color: #ffffff !important;
 background-color: #d2b48c !important;
 border: 1px solid #d2b48c !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
 color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
 background-color: #f5f5dc !important;
 border: 1px solid #d2b48c !important;
 border-top: none !important;
}

.document-panel {
 border: 1px solid #d2b48c;
 margin-bottom: 8px;
}

:deep(.document-panel > .v-expansion-panel-title) {
 background-color: #ffffff !important;
 color: #333333 !important;
}

#documents-panel {
 margin-bottom: 28px;
}

.document-content h2 {
 font-size: 1.0rem;
 font-weight: 600;
 margin-top: 24px;
 margin-bottom: 16px;
 color: #2c3e50;
}

.document-content h3 {
 font-size: 1.25rem;
 font-weight: 600;
 margin-top: 20px;
 margin-bottom: 12px;
 color: #2c3e50;
}

.document-content p {
 margin-bottom: 16px;
}

.document-content .text-caption {
 font-size: 0.875rem;
 color: #666;
 margin-bottom: 24px;
}

.document-content .policy-section {
 padding-left: 24px;
 margin-left: 24px;
}

.document-content .policy-list {
 list-style-type: disc !important;
 padding-left: 40px !important;
}

.checkbox-acceptance-section {
margin-top: 8px;
}

.checkbox-container {
position: relative;
width: 100%;
display: flex;
justify-content: space-between;
align-items: flex-end;
}

.checkbox-date-display {
min-width: 200px;
width: 300px;
text-align: center;
padding-bottom: 4px;
margin-bottom: 7px;
}

.checkbox-current-date {
font-size: 14px;
color: #333;
margin-bottom: 4px;
text-align: center;
}

.checkbox-underline {
width: 100%;
height: 1px;
background-color: #333;
margin: 0 auto;
}

.signature-container {
position: relative;
width: 100%;
}

.signature-input-section {
width: 670px;
}

.signature-date-display {
position: absolute;
right: 0;
bottom: 36px;
width: 300px;
text-align: center;
}

.signature-current-date {
font-size: 14px;
color: #333;
margin-bottom: 4px;
text-align: center;
}

.signature-underline {
width: 100%;
height: 2px;
background-color: #333;
margin: 0 auto;
}

@media (max-width: 600px) {
 .accordion-container {
   padding: 12px;
 }

 .signature-section {
   max-width: 100%;
 }
}

.document-content .policy-section {
padding-left: 24px !important;
margin-left: 24px !important;
position: relative !important;
}

.document-content .policy-list {
list-style-type: disc !important;
padding-left: 40px !important;
margin-left: 24px !important;
position: relative !important;
display: block !important;
}

.document-content .policy-list li {
display: list-item !important;
list-style-type: disc !important;
margin-left: 1em !important;
}

.signature-container {
 position: relative;
 width: 100%;
}

.signature-input-section {
 width: calc(100% - 320px);
}

.signature-date-display {
 position: absolute;
 right: 0;
 bottom: 36px;
 width: 300px;
 text-align: center;
}

.signature-current-date {
 font-size: 14px;
 color: #333;
 margin-bottom: 4px;
 text-align: center;
}

.signature-underline {
 width: 100%;
 height: 2px;
 background-color: #333;
 margin: 0 auto;
}

.signature-section {
 max-width: 670px;
}

.input-container {
 position: relative;
 width: 60%;
}

.signature-date-display {
 position: absolute;
 right: 0;
 bottom: 12px;
 width: 300px;
 text-align: center;
}

.signature-current-date {
 font-size: 14px;
 color: #333;
 margin-bottom: 4px;
 text-align: center;
}

.signature-underline {
 width: 100%;
 height: 2px;
 background-color: #333;
 margin: 0 auto;
}

.input-container {
 position: relative;
 width: 60%;
}

.signature-field {
 width: 100%;
}

.signature-date-display {
 position: absolute;
 left: 170%;
 bottom: 18px;
 width: 300px;
 text-align: center;
}

.signature-field {
 width: 100%; 
 display: flex;
 flex-direction: column;
 gap: 8px;    
}

.signature-date-display {
 position: absolute;
 left: 170%;
 bottom: 75px;
 width: 300px;
 text-align: center;
}

.signature-field .v-btn {
 align-self: flex-start;
 margin-bottom: 8px;    
}
</style>
